import { notification } from 'antd'
import { checkAuth } from 'auth'
import axios from 'axios'
import {
  COOKIE_REFRESH_TOKEN,
  ERROR_CODE,
  ERROR_CODE_RE_LOGIN,
  RES_STATUS
} from 'constants/common'
import { ENV } from 'constants/env'
import { IResponse } from 'interfaces/services'
import Cookies from 'js-cookie'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { removeToken, saveToken } from 'utils'
import { authBaseAxios } from 'utils/api'

const useAxiosService = (ignoreAuth?: boolean) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const get = (url: string, payload?: any, hideToast?: boolean) => {
    const isAuth = handleCheckAuth()
    if (isAuth || ignoreAuth) {
      const httpMethod = authBaseAxios.get
      return handleResponse(
        httpMethod(url, payload),
        httpMethod,
        url,
        payload,
        hideToast
      )
    }
  }

  const post = (url: string, payload: any, hideToast?: boolean) => {
    const isAuth = handleCheckAuth()
    if (isAuth || ignoreAuth) {
      const httpMethod = authBaseAxios.post
      return handleResponse(
        httpMethod(url, payload) as Promise<any>,
        httpMethod,
        url,
        payload,
        hideToast
      )
    }
  }

  const put = (url: string, payload: any, hideToast?: boolean) => {
    const isAuth = handleCheckAuth()
    if (isAuth || ignoreAuth) {
      const httpMethod = authBaseAxios.put
      return handleResponse(
        httpMethod(url, payload),
        httpMethod,
        url,
        payload,
        hideToast
      )
    }
  }

  const patch = (url: string, payload?: any, hideToast?: boolean) => {
    const isAuth = handleCheckAuth()
    if (isAuth || ignoreAuth) {
      const httpMethod = authBaseAxios.patch
      return handleResponse(
        httpMethod(url, payload),
        httpMethod,
        url,
        payload,
        hideToast
      )
    }
  }

  const dele = (url: string, query?: any, hideToast?: boolean) => {
    const isAuth = handleCheckAuth()
    if (isAuth || ignoreAuth) {
      const httpMethod = authBaseAxios.delete
      return handleResponse(
        httpMethod(url, { data: query }),
        httpMethod,
        url,
        {
          data: query
        },
        hideToast
      )
    }
  }

  const all = (apis: Array<any>) => {
    const isAuth = handleCheckAuth()
    if (isAuth || ignoreAuth) {
      return handleResponseAll(apis) as Promise<IResponse<Array<any>>>
    }
  }

  const handleResponse = async (
    promise: Promise<IResponse<any>>,
    callApiAgain: (url: string, payload?: any) => Promise<any>,
    url: string,
    payload?: any,
    hideToast?: boolean
  ) => {
    const res = await promise
    if (!res?._successfully) {
      if (ERROR_CODE_RE_LOGIN.includes(res?._errorCode)) {
        return refreshToken(callApiAgain, url, payload)
          .then((res) => {
            return res
          })
          .catch(() => {
            removeToken()
            navigate('/login')
          })
      } else {
        if (res?._errorCode && !hideToast) {
          notification.error({
            message: res._message
              ? res._message
              : t(`error-code-${res?._errorCode}`)
          })
        }
        return res
      }
    } else {
      return res
    }
  }

  const handleCheckAuth = () => {
    const isAuth = checkAuth()
    return isAuth
  }

  const handleResponseAll = async (apis: Array<any>) => {
    const allRes = await axios
      .all<any>(apis)
      .then((res) => {
        const dataList = res?.map((item) => {
          return item?.data
        })
        const success = res?.every((item) => item?._successfully)
        return {
          ...res[0],
          _successfully: success,
          data: dataList
        } as IResponse<[]>
      })
      .catch(() => {
        return {
          _status: RES_STATUS.FAIL,
          _statusRes: 401,
          _message: 'has-error',
          _successfully: false,
          data: []
        } as IResponse<[]>
      })
    return allRes
  }

  const refreshToken = async (
    callApiAgain: (url: string, payload?: any) => Promise<any>,
    url: string,
    payload?: any
  ) => {
    const refreshToken = Cookies.get(COOKIE_REFRESH_TOKEN)
    const response: any = await axios.get('/clinic/user/refresh-token', {
      headers: { Authorization: `Bearer ${refreshToken}` },
      baseURL: `${ENV.baseApi}/${ENV.versionApi}`
    })
    if (response?.data?.error_code === ERROR_CODE.SUCCESS) {
      saveToken(response.data?.data)
      return callApiAgain(url, payload)
    } else {
      removeToken()
      navigate('/login')
    }
  }

  const getFile = async (url: string) => {
    return await axios({
      url: url,
      method: 'GET',
      responseType: 'blob'
    })
      .then((response) => {
        const downloadUrl = window.URL.createObjectURL(
          new Blob([response.data])
        )
        const link = document.createElement('a')
        link.href = downloadUrl
        link.setAttribute('download', 'Biểu Mẫu Nhập kho.xlsx')
        document.body.appendChild(link)
        link.click()
      })
      .catch((error) => {
        console.error('Error downloading file:', error)
      })
  }

  const getFileService = async (url: string) => {
    return await axios({
      url: url,
      method: 'GET',
      responseType: 'blob'
    })
      .then((response) => {
        const downloadUrl = window.URL.createObjectURL(
          new Blob([response.data])
        )
        const link = document.createElement('a')
        link.href = downloadUrl
        link.setAttribute('download', 'Biểu Mẫu import dịch vụ.xlsx')
        document.body.appendChild(link)
        link.click()
      })
      .catch((error) => {
        console.error('Error downloading file:', error)
      })
  }
  return { get, post, put, all, patch, dele, getFile, getFileService }
}

export default useAxiosService
